import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import DownloadIcon from '@mui/icons-material/Download'
import FilterListIcon from '@mui/icons-material/FilterList'
import PriceComparisonGrid from '../components/dc/PriceComparisonGrid'
import { dcApi } from '../services/dc.api'

export default function DCComparisonPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [minDiscount, setMinDiscount] = useState<number | ''>('')
  const [maxDiscount, setMaxDiscount] = useState<number | ''>('')
  const [showFilters, setShowFilters] = useState(false)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['comparisons', page, search, minDiscount, maxDiscount],
    queryFn: () => dcApi.getComparisons({
      page,
      limit: 50,
      search: search || undefined,
      minDiscount: minDiscount !== '' ? minDiscount : undefined,
      maxDiscount: maxDiscount !== '' ? maxDiscount : undefined
    })
  })

  const handleExport = async () => {
    try {
      const blob = await dcApi.exportToExcel()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `price_comparison_${new Date().toISOString().split('T')[0]}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export error:', err)
    }
  }

  const handleSearch = () => {
    setPage(1)
    refetch()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const clearFilters = () => {
    setSearch('')
    setMinDiscount('')
    setMaxDiscount('')
    setPage(1)
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load comparison data. Please try again.
      </Alert>
    )
  }

  const comparisons = data?.comparisons || []
  const pagination = data?.pagination || { total: 0, totalPages: 0 }
  const hasFilters = search || minDiscount !== '' || maxDiscount !== ''

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Price Comparison
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={comparisons.length === 0}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by item name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSearch}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Chip
                label={`${pagination.total} items`}
                color="primary"
                variant="outlined"
              />
              {hasFilters && (
                <Chip
                  label="Clear Filters"
                  onClick={clearFilters}
                  onDelete={clearFilters}
                />
              )}
            </Box>
          </Grid>

          {/* Extended Filters */}
          {showFilters && (
            <>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min Discount %"
                  value={minDiscount}
                  onChange={(e) => setMinDiscount(e.target.value ? parseFloat(e.target.value) : '')}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Discount %"
                  value={maxDiscount}
                  onChange={(e) => setMaxDiscount(e.target.value ? parseFloat(e.target.value) : '')}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Comparison Grid */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : comparisons.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No comparison data available
          </Typography>
          <Typography color="textSecondary">
            Upload distributor price lists to see comparisons here.
          </Typography>
        </Paper>
      ) : (
        <PriceComparisonGrid
          comparisons={comparisons}
          page={page}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
        />
      )}
    </Box>
  )
}
